// Add-a-Number — classic "audience adds three numbers and the total
// matches my pre-written prediction" force, wired into the (+) compose
// button of the fake Notes app.
//
// Performance flow:
//   1. Magician taps (+) → blank note opens.
//   2. Spectator 1 writes `1. 234`, hits return, hands the phone over.
//   3. Spectator 2 writes `2. 567`, hits return, hands over.
//   4. Spectator 3 writes `3. 789`. The configured trigger fires
//      (keyboard dismiss / handoff motion / place-down), silently
//      replacing the digits on one line so the eventual sum lands on
//      the magician's preset force total.
//
// The trigger is chosen in Settings so the magician never has to touch
// the phone during the swap.

import { STORAGE } from "./storage";

export const DEFAULT_FORCE_TOTAL = 21024;
export const ADDNUM_TRIGGERS = ["done", "handoff", "placedown"];

/** Read the full Add-a-Number configuration in one go. */
export function getAddNumberConfig() {
  const forceRaw = localStorage.getItem(STORAGE.ADDNUM_FORCE_TOTAL);
  const force = parseInt(forceRaw || "", 10);
  const swapRaw = parseInt(
    localStorage.getItem(STORAGE.ADDNUM_SWAP_POSITION) || "3",
    10,
  );
  const trigger = localStorage.getItem(STORAGE.ADDNUM_TRIGGER);
  return {
    enabled: localStorage.getItem(STORAGE.ADDNUM_ENABLED) === "1",
    forceTotal: Number.isFinite(force) && force > 0 ? force : DEFAULT_FORCE_TOTAL,
    swapPosition: swapRaw === 1 || swapRaw === 2 ? swapRaw : 3,
    trigger: ADDNUM_TRIGGERS.includes(trigger) ? trigger : "done",
  };
}

/**
 * Parse a textarea body looking for three numbered lines (1. / 2. / 3.).
 * Each line's leading marker can be `.`, `)`, `:` or just whitespace. The
 * value is the first run of digits found on the line.
 *
 * Returns `{ 1: "234", 2: "567", 3: "789" }` (string digits) or `null`
 * entries for missing rows.
 */
export function parseNumberedLines(text) {
  const lines = String(text || "").split(/\r?\n/);
  const out = { 1: null, 2: null, 3: null };
  for (const line of lines) {
    const m = /^\s*([123])[.):\s]\s*(.*)$/.exec(line);
    if (!m) continue;
    const pos = parseInt(m[1], 10);
    if (out[pos] !== null) continue; // first occurrence wins
    const digits = (m[2].match(/\d+/) || [""])[0];
    if (digits.length > 0) out[pos] = digits;
  }
  return out;
}

/**
 * Replace the first run of digits on the line labelled `position` (1/2/3)
 * with `newDigits`. The rest of the line — including the marker, the
 * spectator's punctuation, and any trailing text — is preserved exactly.
 *
 * If no matching line exists, the body is returned unchanged.
 */
export function replaceNumberOnLine(text, position, newDigits) {
  const lines = String(text || "").split(/\r?\n/);
  let replaced = false;
  const out = lines.map((line) => {
    if (replaced) return line;
    const m = /^(\s*)([123])([.):\s])(\s*)(.*)$/.exec(line);
    if (!m) return line;
    if (parseInt(m[2], 10) !== position) return line;
    const rest = m[5];
    if (!/\d+/.test(rest)) return line;
    replaced = true;
    return m[1] + m[2] + m[3] + m[4] + rest.replace(/\d+/, String(newDigits));
  });
  return { text: out.join("\n"), replaced };
}

/**
 * Compute the swap to apply given the current numbers and the magician's
 * config. Falls back to a two-row swap (5c) when the primary single-row
 * substitution would land on a non-positive value.
 *
 * Returns `{ updates }` where `updates` is `{position: newDigits}`. Empty
 * object → silent abort (impossible force, keep numbers as the
 * spectators typed them).
 */
export function computeAddNumberSwap(nums, config) {
  const T = config.forceTotal;
  const p = config.swapPosition; // 1..3
  const parsed = [1, 2, 3].map((i) => {
    const v = parseInt(nums[i] || "", 10);
    return Number.isFinite(v) ? v : null;
  });
  if (parsed.some((v) => v === null)) return { updates: {} };

  // --- Primary: replace just the swap row -------------------------------
  const sumOthers = parsed.reduce((s, v, i) => (i + 1 === p ? s : s + v), 0);
  const primary = T - sumOthers;
  if (primary > 0) {
    return { updates: { [p]: String(primary) } };
  }

  // --- Two-row fallback (5c) -------------------------------------------
  // Pick the row right after the swap row (cyclic) as the second one to
  // rewrite. Keep the third row exactly as the spectator typed it.
  const second = (p % 3) + 1; // 1->2, 2->3, 3->1
  const keepIdx = [1, 2, 3].find((i) => i !== p && i !== second);
  const remaining = parsed[keepIdx - 1];
  const R = T - remaining;
  if (R < 200) return { updates: {} };

  // Split R between the two unknowns. Pick the swap row's value roughly
  // in the middle so the result looks natural. Add a small jitter so
  // repeat performances don't always show the same pair.
  const half = Math.floor(R / 2);
  const jitter = Math.floor((Math.random() - 0.5) * Math.floor(R / 4));
  let A = Math.max(100, Math.min(R - 100, half + jitter));
  let B = R - A;
  if (A <= 0 || B <= 0) return { updates: {} };
  return { updates: { [p]: String(A), [second]: String(B) } };
}

/** True once all three numbered lines have parsed integer values. */
export function isAddNumberReady(nums) {
  return [1, 2, 3].every((i) => {
    const v = parseInt(nums[i] || "", 10);
    return Number.isFinite(v) && v > 0;
  });
}
